import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { getColorScheme } from '../theme/colorSchemes';
import { translator } from '../assessments/crs/i18n';
import { getLabourMarketPoints, isLabourMarketFactorInForce, labourMarketMax } from '../assessments/crs/labourMarket';
import CrsNocPicker from './CrsNocPicker';

// ============================================================
// CLB CONVERSION TABLES
// ============================================================
const CLB_TABLES = {
  "CELPIP-G": {
    speaking: { "10+": 10, 10: 10, 9: 9, 8: 8, 7: 7, 6: 6, 5: 5, 4: 4, "3-": 0, M: 0, 3: 0, 2: 0, 1: 0, 0: 0 },
    listening: { "10+": 10, 10: 10, 9: 9, 8: 8, 7: 7, 6: 6, 5: 5, 4: 4, "3-": 0, M: 0, 3: 0, 2: 0, 1: 0, 0: 0 },
    reading: { "10+": 10, 10: 10, 9: 9, 8: 8, 7: 7, 6: 6, 5: 5, 4: 4, "3-": 0, M: 0, 3: 0, 2: 0, 1: 0, 0: 0 },
    writing: { "10+": 10, 10: 10, 9: 9, 8: 8, 7: 7, 6: 6, 5: 5, 4: 4, "3-": 0, M: 0, 3: 0, 2: 0, 1: 0, 0: 0 },
    options: {
      speaking: ["10+", "9", "8", "7", "6", "5", "4", "M", "3", "2", "1", "0"],
      listening: ["10+", "9", "8", "7", "6", "5", "4", "M", "3", "2", "1", "0"],
      reading: ["10+", "9", "8", "7", "6", "5", "4", "M", "3", "2", "1", "0"],
      writing: ["10+", "9", "8", "7", "6", "5", "4", "M", "3", "2", "1", "0"],
    },
  },
  IELTS: {
    speaking: { "7.5+": 10, 7.5: 10, 7: 9, 6.5: 8, 6: 7, 5.5: 6, 5: 5, 4.5: 4, 4: 4, "3.5-": 0, 3.5: 0, 3: 0, 0: 0 },
    listening: { "8.5+": 10, 8.5: 10, 8: 9, 7.5: 8, 6: 7, 5.5: 6, 5: 5, 4.5: 4, "3.5-": 0, 3.5: 0, 3: 0, 0: 0 },
    reading: { "8+": 10, 8: 10, 7: 9, 6.5: 8, 6: 7, 5: 6, 4: 5, 3.5: 4, "3-": 0, 3: 0, 0: 0 },
    writing: { "7.5+": 10, 7.5: 10, 7: 9, 6.5: 8, 6: 7, 5.5: 6, 5: 5, 4: 4, "3.5-": 0, 3.5: 0, 3: 0, 0: 0 },
    options: {
      speaking: ["7.5+", "7.5", "7.0", "6.5", "6.0", "5.5", "5.0", "4.5", "4.0", "3.5", "3.0", "0"],
      listening: ["8.5+", "8.5", "8.0", "7.5", "6.0", "5.5", "5.0", "4.5", "3.5", "3.0", "0"],
      reading: ["8.0+", "8.0", "7.0", "6.5", "6.0", "5.0", "4.0", "3.5", "3.0", "0"],
      writing: ["7.5+", "7.5", "7.0", "6.5", "6.0", "5.5", "5.0", "4.0", "3.5", "3.0", "0"],
    },
  },
  "PTE Core": {
    speaking: { "89+": 10, 89: 10, 84: 10, 76: 9, 68: 8, 59: 7, 51: 6, 42: 5, 34: 4, "0": 0 },
    listening: { "89+": 10, 89: 10, 82: 10, 71: 9, 60: 8, 50: 7, 39: 6, 28: 5, 18: 4, "0": 0 },
    reading: { "88+": 10, 88: 10, 78: 10, 69: 9, 60: 8, 51: 7, 42: 6, 33: 5, 24: 4, "0": 0 },
    writing: { "90+": 10, 90: 10, 88: 10, 79: 9, 69: 8, 60: 7, 51: 6, 41: 5, 32: 4, "0": 0 },
    options: {
      speaking: ["89-90", "84-88", "76-83", "68-75", "59-67", "51-58", "42-50", "34-41", "0-33"],
      listening: ["89-90", "82-88", "71-81", "60-70", "50-59", "39-49", "28-38", "18-27", "0-17"],
      reading: ["88-90", "78-87", "69-77", "60-68", "51-59", "42-50", "33-41", "24-32", "0-23"],
      writing: ["90", "88-89", "79-87", "69-78", "60-68", "51-59", "41-50", "32-40", "0-31"],
    },
    optionToCLB: {
      speaking: { "89-90": 10, "84-88": 10, "76-83": 9, "68-75": 8, "59-67": 7, "51-58": 6, "42-50": 5, "34-41": 4, "0-33": 0 },
      listening: { "89-90": 10, "82-88": 10, "71-81": 9, "60-70": 8, "50-59": 7, "39-49": 6, "28-38": 5, "18-27": 4, "0-17": 0 },
      reading: { "88-90": 10, "78-87": 10, "69-77": 9, "60-68": 8, "51-59": 7, "42-50": 6, "33-41": 5, "24-32": 4, "0-23": 0 },
      writing: { "90": 10, "88-89": 10, "79-87": 9, "69-78": 8, "60-68": 7, "51-59": 6, "41-50": 5, "32-40": 4, "0-31": 0 },
    },
  },
  "TEF Canada": {
    speaking: { 393: 10, 371: 9, 349: 8, 310: 7, 271: 6, 226: 5, 181: 4, "0": 0 },
    listening: { 316: 10, 298: 9, 280: 8, 249: 7, 217: 6, 181: 5, 145: 4, "0": 0 },
    reading: { 263: 10, 248: 9, 233: 8, 207: 7, 181: 6, 151: 5, 121: 4, "0": 0 },
    writing: { 393: 10, 371: 9, 349: 8, 310: 7, 271: 6, 226: 5, 181: 4, "0": 0 },
    options: {
      speaking: ["393-450", "371-392", "349-370", "310-348", "271-309", "226-270", "181-225", "0-180"],
      listening: ["316-360", "298-315", "280-297", "249-279", "217-248", "181-216", "145-180", "0-144"],
      reading: ["263-300", "248-262", "233-247", "207-232", "181-206", "151-180", "121-150", "0-120"],
      writing: ["393-450", "371-392", "349-370", "310-348", "271-309", "226-270", "181-225", "0-180"],
    },
    optionToCLB: {
      speaking: { "393-450": 10, "371-392": 9, "349-370": 8, "310-348": 7, "271-309": 6, "226-270": 5, "181-225": 4, "0-180": 0 },
      listening: { "316-360": 10, "298-315": 9, "280-297": 8, "249-279": 7, "217-248": 6, "181-216": 5, "145-180": 4, "0-144": 0 },
      reading: { "263-300": 10, "248-262": 9, "233-247": 8, "207-232": 7, "181-206": 6, "151-180": 5, "121-150": 4, "0-120": 0 },
      writing: { "393-450": 10, "371-392": 9, "349-370": 8, "310-348": 7, "271-309": 6, "226-270": 5, "181-225": 4, "0-180": 0 },
    },
  },
  "TCF Canada": {
    speaking: { 16: 10, 14: 9, 12: 8, 10: 7, 7: 6, 6: 5, 4: 4, "0": 0 },
    listening: { 549: 10, 503: 9, 458: 8, 398: 7, 331: 6, 280: 5, 217: 4, "0": 0 },
    reading: { 549: 10, 524: 9, 499: 8, 453: 7, 406: 6, 375: 5, 342: 4, "0": 0 },
    writing: { 16: 10, 14: 9, 12: 8, 10: 7, 7: 6, 6: 5, 4: 4, "0": 0 },
    options: {
      speaking: ["16-20", "14-15", "12-13", "10-11", "7-9", "6", "4-5", "0-3"],
      listening: ["549-699", "503-548", "458-502", "398-457", "331-397", "280-330", "217-279", "0-216"],
      reading: ["549-699", "524-548", "499-523", "453-498", "406-452", "375-405", "342-374", "0-341"],
      writing: ["16-20", "14-15", "12-13", "10-11", "7-9", "6", "4-5", "0-3"],
    },
    optionToCLB: {
      speaking: { "16-20": 10, "14-15": 9, "12-13": 8, "10-11": 7, "7-9": 6, "6": 5, "4-5": 4, "0-3": 0 },
      listening: { "549-699": 10, "503-548": 9, "458-502": 8, "398-457": 7, "331-397": 6, "280-330": 5, "217-279": 4, "0-216": 0 },
      reading: { "549-699": 10, "524-548": 9, "499-523": 8, "453-498": 7, "406-452": 6, "375-405": 5, "342-374": 4, "0-341": 0 },
      writing: { "16-20": 10, "14-15": 9, "12-13": 8, "10-11": 7, "7-9": 6, "6": 5, "4-5": 4, "0-3": 0 },
    },
  },
};

function getCLB(testType, ability, selectedOption) {
  if (!testType || !selectedOption) return 0;
  const table = CLB_TABLES[testType];
  if (!table) return 0;
  if (table.optionToCLB) {
    return table.optionToCLB[ability]?.[selectedOption] ?? 0;
  }
  const scoreMap = table[ability];
  if (scoreMap[selectedOption] !== undefined) return scoreMap[selectedOption];
  const num = parseFloat(selectedOption);
  if (scoreMap[num] !== undefined) return scoreMap[num];
  if (selectedOption.endsWith("+")) return scoreMap[selectedOption] ?? 10;
  return 0;
}

function getTestOptions(testType, ability) {
  if (!testType) return [];
  return CLB_TABLES[testType]?.options?.[ability] ?? [];
}

function isFrenchTest(testType) {
  return testType === "TEF Canada" || testType === "TCF Canada";
}

// ============================================================
// SCORING ENGINE
// ============================================================
function getAgePoints(age, hasSpouse) {
  const table = hasSpouse
    ? { 17: 0, 18: 90, 19: 95, 20: 100, 30: 95, 31: 90, 32: 85, 33: 80, 34: 75, 35: 70, 36: 65, 37: 60, 38: 55, 39: 50, 40: 45, 41: 35, 42: 25, 43: 15, 44: 5, 45: 0 }
    : { 17: 0, 18: 99, 19: 105, 20: 110, 30: 105, 31: 99, 32: 94, 33: 88, 34: 83, 35: 77, 36: 72, 37: 66, 38: 61, 39: 55, 40: 50, 41: 39, 42: 28, 43: 17, 44: 6, 45: 0 };
  if (age <= 17 || age >= 45) return 0;
  if (age >= 20 && age <= 29) return hasSpouse ? 100 : 110;
  return table[age] ?? 0;
}

// Labels are i18n KEYS, resolved per render — these are IRCC credential
// definitions, so they stay English until a locale's version is reviewed.
const EDU_LEVELS = [
  { value: "none", labelKey: "eduNone" },
  { value: "secondary", labelKey: "eduSecondary" },
  { value: "one_year", labelKey: "eduOneYear" },
  { value: "two_year", labelKey: "eduTwoYear" },
  { value: "bachelors", labelKey: "eduBachelors" },
  { value: "two_or_more", labelKey: "eduTwoOrMore" },
  { value: "masters", labelKey: "eduMasters" },
  { value: "phd", labelKey: "eduPhd" },
];

function getEducationPoints(level, hasSpouse) {
  const table = hasSpouse
    ? { none: 0, secondary: 28, one_year: 84, two_year: 91, bachelors: 112, two_or_more: 119, masters: 126, phd: 140 }
    : { none: 0, secondary: 30, one_year: 90, two_year: 98, bachelors: 120, two_or_more: 128, masters: 135, phd: 150 };
  return table[level] ?? 0;
}

function getFirstLangPoints(clbs, hasSpouse) {
  const tableWithSpouse = { 0: 0, 4: 6, 5: 6, 6: 8, 7: 16, 8: 22, 9: 29, 10: 32 };
  const tableWithout = { 0: 0, 4: 6, 5: 6, 6: 9, 7: 17, 8: 23, 9: 31, 10: 34 };
  const table = hasSpouse ? tableWithSpouse : tableWithout;
  let total = 0;
  for (const ability of ["speaking", "listening", "reading", "writing"]) {
    const clb = clbs[ability] ?? 0;
    if (clb < 4) { total += 0; continue; }
    if (clb >= 10) { total += table[10]; continue; }
    total += table[clb] ?? 0;
  }
  return total;
}

function getSecondLangPoints(clbs, hasSpouse) {
  if (!clbs) return 0;
  const table = { 0: 0, 4: 0, 5: 1, 6: 1, 7: 3, 8: 3, 9: 6, 10: 6 };
  const maxTotal = hasSpouse ? 22 : 24;
  let total = 0;
  for (const ability of ["speaking", "listening", "reading", "writing"]) {
    const clb = clbs[ability] ?? 0;
    if (clb <= 4) { total += 0; continue; }
    if (clb >= 9) { total += 6; continue; }
    total += table[clb] ?? 0;
  }
  return Math.min(total, maxTotal);
}

function getCanadianWorkPoints(years, hasSpouse) {
  const table = hasSpouse
    ? { 0: 0, 1: 35, 2: 46, 3: 56, 4: 63, 5: 70 }
    : { 0: 0, 1: 40, 2: 53, 3: 64, 4: 72, 5: 80 };
  return table[Math.min(years, 5)] ?? 0;
}

function getSpouseEducationPoints(level) {
  const table = { none: 0, secondary: 2, one_year: 6, two_year: 7, bachelors: 8, two_or_more: 9, masters: 10, phd: 10 };
  return table[level] ?? 0;
}

function getSpouseLangPoints(clbs) {
  if (!clbs) return 0;
  const table = { 0: 0, 4: 0, 5: 1, 6: 1, 7: 3, 8: 3, 9: 5, 10: 5 };
  let total = 0;
  for (const ability of ["speaking", "listening", "reading", "writing"]) {
    const clb = clbs[ability] ?? 0;
    if (clb <= 4) continue;
    if (clb >= 9) { total += 5; continue; }
    total += table[clb] ?? 0;
  }
  return Math.min(total, 20);
}

function getSpouseCanWorkPoints(years) {
  const table = { 0: 0, 1: 5, 2: 7, 3: 8, 4: 9, 5: 10 };
  return table[Math.min(years, 5)] ?? 0;
}

function eduNumeric(level) {
  const map = { none: 0, secondary: 1, one_year: 2, two_year: 2, bachelors: 3, two_or_more: 4, masters: 5, phd: 6 };
  return map[level] ?? 0;
}

function getSkillTransferPoints(education, firstLangCLBs, canadianWorkYears, foreignWorkYears, hasCertQual) {
  let total = 0;
  const eduLevel = eduNumeric(education);
  const hasPostSecondary = eduLevel >= 2;
  const hasHighEdu = eduLevel >= 4;
  const minCLB = Math.min(firstLangCLBs.speaking, firstLangCLBs.listening, firstLangCLBs.reading, firstLangCLBs.writing);
  const hasCLB7 = minCLB >= 7;
  const hasCLB9 = minCLB >= 9;
  const hasCLB5 = minCLB >= 5;
  const canWork1 = canadianWorkYears >= 1;
  const canWork2 = canadianWorkYears >= 2;
  const forWork1 = foreignWorkYears >= 1;
  const forWork3 = foreignWorkYears >= 3;

  let eduLang = 0;
  if (hasPostSecondary && hasCLB7) {
    if (hasHighEdu && hasCLB9) eduLang = 50;
    else if (hasHighEdu && hasCLB7) eduLang = 25;
    else if (hasCLB9) eduLang = 25;
    else eduLang = 13;
  }

  let eduWork = 0;
  if (hasPostSecondary && canWork1) {
    if (hasHighEdu && canWork2) eduWork = 50;
    else if (hasHighEdu && canWork1) eduWork = 25;
    else if (canWork2) eduWork = 25;
    else eduWork = 13;
  }

  total += Math.min(eduLang + eduWork, 50);

  let forLang = 0;
  if (forWork1 && hasCLB7) {
    if (forWork3 && hasCLB9) forLang = 50;
    else if (forWork3 && hasCLB7) forLang = 25;
    else if (hasCLB9) forLang = 25;
    else forLang = 13;
  }

  let forCan = 0;
  if (forWork1 && canWork1) {
    if (forWork3 && canWork2) forCan = 50;
    else if (forWork3 && canWork1) forCan = 25;
    else if (canWork2) forCan = 25;
    else forCan = 13;
  }

  total += Math.min(forLang + forCan, 50);

  if (hasCertQual) {
    if (hasCLB7) total += 50;
    else if (hasCLB5) total += 25;
  }

  return Math.min(total, 100);
}

function getAdditionalPoints({ pnp, sibling, firstLangCLBs, secondLangCLBs, firstLangTest, secondLangTest, canadianEdu }) {
  let total = 0;
  if (pnp) total += 600;
  if (sibling) total += 15;

  const isFrenchFirst = isFrenchTest(firstLangTest);
  const isFrenchSecond = isFrenchTest(secondLangTest);
  let frenchCLBs = null;
  let englishCLBs = null;

  if (isFrenchFirst) { frenchCLBs = firstLangCLBs; englishCLBs = secondLangCLBs; }
  else if (isFrenchSecond) { frenchCLBs = secondLangCLBs; englishCLBs = firstLangCLBs; }

  if (frenchCLBs) {
    const frenchMin = Math.min(frenchCLBs.speaking ?? 0, frenchCLBs.listening ?? 0, frenchCLBs.reading ?? 0, frenchCLBs.writing ?? 0);
    if (frenchMin >= 7) {
      if (englishCLBs) {
        const engMin = Math.min(englishCLBs.speaking ?? 0, englishCLBs.listening ?? 0, englishCLBs.reading ?? 0, englishCLBs.writing ?? 0);
        total += engMin >= 5 ? 50 : 25;
      } else {
        total += 25;
      }
    }
  }

  if (canadianEdu === "short") total += 15;
  if (canadianEdu === "long") total += 30;

  return total;
}

export function calculateCRS(answers) {
  const hasSpouse = answers.hasSpouse;
  const agePoints = getAgePoints(answers.age, hasSpouse);
  const eduPoints = getEducationPoints(answers.education, hasSpouse);
  const firstLangPoints = getFirstLangPoints(answers.firstLangCLBs, hasSpouse);
  const secondLangPoints = getSecondLangPoints(answers.secondLangCLBs, hasSpouse);
  const canWorkPoints = getCanadianWorkPoints(answers.canadianWork, hasSpouse);

  // From November 3, 2026 the Canadian work experience points move into the Labour
  // Market Integration factor (work length + high-wage points, OR a job offer —
  // the higher counts). Before that date the old core factor applies unchanged.
  const lmiInForce = answers.lmiInForce ?? isLabourMarketFactorInForce();
  const labourMarket = lmiInForce
    ? { ...getLabourMarketPoints({ workLengthPoints: canWorkPoints, highWageWorkNoc: answers.highWageWorkNoc, jobOfferNoc: answers.jobOfferNoc }), max: labourMarketMax(hasSpouse) }
    : null;
  const coreTotal = agePoints + eduPoints + firstLangPoints + secondLangPoints + (labourMarket ? 0 : canWorkPoints);

  let spouseTotal = 0, spouseEduPts = 0, spouseLangPts = 0, spouseWorkPts = 0;
  if (hasSpouse) {
    spouseEduPts = getSpouseEducationPoints(answers.spouseEducation);
    spouseLangPts = getSpouseLangPoints(answers.spouseLangCLBs);
    spouseWorkPts = getSpouseCanWorkPoints(answers.spouseCanWork);
    spouseTotal = spouseEduPts + spouseLangPts + spouseWorkPts;
  }

  const skillPoints = getSkillTransferPoints(answers.education, answers.firstLangCLBs, answers.canadianWork, answers.foreignWork, answers.certQual);
  const additionalPoints = getAdditionalPoints({
    pnp: answers.pnp, sibling: answers.sibling,
    firstLangCLBs: answers.firstLangCLBs, secondLangCLBs: answers.secondLangCLBs,
    firstLangTest: answers.firstLangTest, secondLangTest: answers.secondLangTest,
    canadianEdu: answers.canadianEdu,
  });

  return {
    total: coreTotal + (labourMarket ? labourMarket.total : 0) + spouseTotal + skillPoints + additionalPoints,
    core: { total: coreTotal, age: agePoints, education: eduPoints, firstLang: firstLangPoints, secondLang: secondLangPoints, canadianWork: labourMarket ? null : canWorkPoints },
    labourMarket,
    // Sum of the section maxima: 1,200 before November 3, 2026; after it the
    // Labour Market Integration factor (140 / 150) replaces Canadian work (70 / 80).
    maxScore: labourMarket ? (hasSpouse ? 390 + 40 : 420) + labourMarket.max + 100 + 600 : 1200,
    rulesVersion: lmiInForce ? 'crs-2026-11-03' : 'crs-2025-03-25',
    spouse: { total: spouseTotal, education: spouseEduPts, language: spouseLangPts, work: spouseWorkPts },
    skill: skillPoints,
    additional: additionalPoints,
  };
}

// ============================================================
// STYLED COMPONENTS
// ============================================================
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  padding: 16px;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  animation: ${fadeIn} 0.3s ease-out;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
  max-width: 100%;
  width: 100%;
  min-width: 0;

  *, *::before, *::after {
    box-sizing: border-box;
  }

  &::-webkit-scrollbar { display: none; }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const StepHeader = styled.div`
  margin-bottom: 16px;
  min-width: 0;
`;

const StepTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 4px 0;
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.95)' : '#1a2332'};
`;

const StepNumber = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${p => {
    const c = getColorScheme(p.$scheme);
    return `linear-gradient(135deg, ${c.primary}, ${c.primaryDark})`;
  }};
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
`;

const StepSubtitle = styled.p`
  margin: 0 0 0 36px;
  font-size: 11px;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.5)' : '#6b7a8d'};
  line-height: 1.4;
`;

const ProgressBarContainer = styled.div`
  margin-bottom: 16px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const ProgressBarTrack = styled.div`
  height: 5px;
  border-radius: 3px;
  background: ${p => p.$darkMode ? '#333' : '#f1f5f9'};
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  border-radius: 3px;
  background: ${p => {
    const c = getColorScheme(p.$scheme);
    return `linear-gradient(90deg, ${c.primary}, ${c.primaryLight || c.primary})`;
  }};
  width: ${p => p.$pct}%;
  transition: width 0.4s ease;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 11px;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.5)' : '#6b7a8d'};
`;

const SelectField = styled.div`
  margin-bottom: 14px;
  width: 100%;
  min-width: 0;
  overflow: visible;
  box-sizing: border-box;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  font-size: 12px;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.9)' : '#1a2332'};
`;

const HelpText = styled.p`
  margin: 0 0 5px 0;
  font-size: 11px;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.5)' : '#6b7a8d'};
  line-height: 1.4;
`;

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const DropdownTrigger = styled.div`
  width: 100%;
  padding: 10px 32px 10px 12px;
  border-radius: 10px;
  border: 2px solid ${p => {
    const c = getColorScheme(p.$scheme);
    return p.$isOpen
      ? c.primary
      : p.$darkMode ? c.borderAccent : c.borderAccent;
  }};
  font-size: 12px;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  background: ${p => {
    const c = getColorScheme(p.$scheme);
    return p.$darkMode ? c.bgInput : '#fff';
  }};
  color: ${p => {
    if (!p.$hasValue) return p.$darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8';
    return p.$darkMode ? 'rgba(255,255,255,0.9)' : '#1a2332';
  }};
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  user-select: none;
  position: relative;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%) ${p => p.$isOpen ? 'rotate(180deg)' : 'rotate(0)'};
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid ${p => p.$darkMode ? 'rgba(255,255,255,0.5)' : '#64748b'};
    transition: transform 0.2s;
  }
`;

const DropdownList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  border-radius: 10px;
  border: 2px solid ${p => {
    const c = getColorScheme(p.$scheme);
    return p.$darkMode ? c.borderAccent : c.borderAccent;
  }};
  background: ${p => p.$darkMode ? '#1e1e1e' : '#fff'};
  z-index: 50;
  box-shadow: ${p => p.$darkMode
    ? '0 8px 24px rgba(0,0,0,0.6)'
    : '0 8px 24px rgba(0,0,0,0.12)'};

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${p => p.$darkMode ? '#444' : '#ccc'};
    border-radius: 3px;
  }
`;

const DropdownItem = styled.div`
  padding: 9px 12px;
  font-size: 12px;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.9)' : '#1a2332'};
  cursor: pointer;
  transition: background 0.15s;
  background: ${p => p.$isSelected
    ? (p.$darkMode ? '#2a2a2a' : '#f0f4f8')
    : 'transparent'};

  &:hover {
    background: ${p => {
      const c = getColorScheme(p.$scheme);
      return p.$darkMode ? '#333' : '#f0f4f8';
    }};
  }

  &:first-child { border-radius: 8px 8px 0 0; }
  &:last-child { border-radius: 0 0 8px 8px; }
`;

const LangGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 8px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const NavRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  gap: 10px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const BackBtn = styled.button`
  padding: 10px 20px;
  border-radius: 10px;
  border: 2px solid ${p => p.$darkMode ? '#444' : '#e2e8f0'};
  background: transparent;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.7)' : '#64748b'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  transition: all 0.2s;

  &:hover { background: ${p => p.$darkMode ? '#333' : '#f1f5f9'}; }
`;

const NextBtn = styled.button`
  padding: 10px 24px;
  border-radius: 10px;
  border: none;
  background: ${p => {
    if (p.disabled) return p.$darkMode ? '#333' : '#e2e8f0';
    const c = getColorScheme(p.$scheme);
    return `linear-gradient(135deg, ${c.primary}, ${c.primaryDark})`;
  }};
  color: ${p => p.disabled ? (p.$darkMode ? '#666' : '#94a3b8') : '#fff'};
  font-size: 13px;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
  box-shadow: ${p => {
    if (p.disabled) return 'none';
    const c = getColorScheme(p.$scheme);
    return `0 4px 12px ${c.shadowColor}`;
  }};

  &:hover:not(:disabled) { transform: translateY(-1px); }
`;

// ============================================================
// STEP DEFINITIONS
// ============================================================
const STEPS = ["marital", "age", "education", "language_first", "language_second", "work", "additional", "spouse"];

// ============================================================
// SHARED OPTION BUILDERS
// ============================================================
const YES_NO = tr => ([{ value: "yes", label: tr('yes') }, { value: "no", label: tr('no') }]);
const NO_YES = tr => ([{ value: "no", label: tr('no') }, { value: "yes", label: tr('yes') }]);
const YEARS_5 = tr => ([
  { value: "0", label: tr('yearsNone') }, { value: "1", label: tr('years1') },
  { value: "2", label: tr('years2') }, { value: "3", label: tr('years3') },
  { value: "4", label: tr('years4') }, { value: "5", label: tr('years5plus') },
]);
const YEARS_3 = tr => ([
  { value: "0", label: tr('yearsNone') }, { value: "1", label: tr('years1') },
  { value: "2", label: tr('years2') }, { value: "3", label: tr('years3plus') },
]);

// ============================================================
// COMPONENT
// ============================================================
const CRSCalculator = ({ onComplete, onBack, darkMode = false, scheme = 'blue', language = 'en' }) => {
  const tr = translator(language);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    maritalStatus: "", spouseIsCitizen: "", spouseComing: "", hasSpouse: false,
    age: "", education: "", hasCanadianEdu: "", canadianEduLevel: "",
    firstLangTest: "", firstLangScores: {}, hasSecondLang: false,
    secondLangTest: "", secondLangScores: {},
    canadianWork: 0, foreignWork: 0, certQual: false,
    highWageWorkNoc: "", highWageWorkTitle: "",
    jobOffer: false, jobOfferNoc: "", jobOfferTitle: "", pnp: false, sibling: false,
    spouseEducation: "", spouseCanWork: 0, spouseLangTest: "", spouseLangScores: {},
  });

  const update = (field, value) => setAnswers(prev => ({ ...prev, [field]: value }));
  const lmiInForce = isLabourMarketFactorInForce();

  // Resolved per render so a language change re-labels immediately.
  const eduLevels = EDU_LEVELS.map(l => ({ value: l.value, label: tr(l.labelKey) }));

  const needsSpouse = (() => {
    const ms = answers.maritalStatus;
    if (ms !== "married" && ms !== "commonlaw") return false;
    if (answers.spouseIsCitizen === "yes") return false;
    if (answers.spouseComing === "no") return false;
    return true;
  })();

  const actualSteps = STEPS.filter(s => {
    if (s === "spouse" && !needsSpouse) return false;
    return true;
  });

  const currentStepName = actualSteps[step] ?? "results";
  const totalSteps = actualSteps.length;

  const computeCLBs = (testType, scores) => {
    const clbs = {};
    for (const ab of ["speaking", "listening", "reading", "writing"]) {
      clbs[ab] = getCLB(testType, ab, scores?.[ab]);
    }
    return clbs;
  };

  const handleCalculate = () => {
    const firstLangCLBs = computeCLBs(answers.firstLangTest, answers.firstLangScores);
    const secondLangCLBs = answers.hasSecondLang ? computeCLBs(answers.secondLangTest, answers.secondLangScores) : null;
    const spouseLangCLBs = needsSpouse ? computeCLBs(answers.spouseLangTest, answers.spouseLangScores) : null;

    let canadianEdu = "none";
    if (answers.hasCanadianEdu === "yes") {
      canadianEdu = answers.canadianEduLevel === "long" ? "long" : answers.canadianEduLevel === "short" ? "short" : "none";
    }

    const input = {
      hasSpouse: needsSpouse, age: parseInt(answers.age) || 0,
      education: answers.education, firstLangCLBs, secondLangCLBs,
      firstLangTest: answers.firstLangTest, secondLangTest: answers.secondLangTest,
      canadianWork: answers.canadianWork, foreignWork: answers.foreignWork,
      certQual: answers.certQual, pnp: answers.pnp, sibling: answers.sibling,
      highWageWorkNoc: answers.canadianWork >= 1 ? answers.highWageWorkNoc : "",
      jobOfferNoc: answers.jobOffer ? answers.jobOfferNoc : "",
      spouseEducation: answers.spouseEducation, spouseCanWork: answers.spouseCanWork,
      spouseLangCLBs, canadianEdu,
    };

    const result = calculateCRS(input);
    result.hasSpouse = needsSpouse;
    onComplete(result, answers);
  };

  const goNext = () => {
    if (step >= actualSteps.length - 1) {
      handleCalculate();
      return;
    }
    setStep(s => Math.min(s + 1, actualSteps.length - 1));
  };

  const goBack = () => {
    if (step === 0) { onBack && onBack(); return; }
    setStep(s => Math.max(s - 1, 0));
  };

  const canProceed = (() => {
    switch (currentStepName) {
      case "marital": {
        if (!answers.maritalStatus) return false;
        const isP = answers.maritalStatus === "married" || answers.maritalStatus === "commonlaw";
        if (isP && !answers.spouseIsCitizen) return false;
        if (isP && answers.spouseIsCitizen === "no" && !answers.spouseComing) return false;
        return true;
      }
      case "age": return !!answers.age;
      case "education": {
        if (!answers.education) return false;
        if (!answers.hasCanadianEdu) return false;
        if (answers.hasCanadianEdu === "yes" && !answers.canadianEduLevel) return false;
        return true;
      }
      case "language_first": {
        if (!answers.firstLangTest) return false;
        const s = answers.firstLangScores;
        return s.speaking && s.listening && s.reading && s.writing;
      }
      case "language_second": return true;
      case "work": return true;
      case "additional": return true;
      case "spouse": return true;
      default: return true;
    }
  })();

  const CustomDropdown = ({ value, onChange, options, placeholder, darkMode: dm, scheme: sc }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
      const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); };
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const selectedOpt = options.find(o => (typeof o === 'object' ? o.value : o) === value);
    const displayLabel = selectedOpt
      ? (typeof selectedOpt === 'object' ? selectedOpt.label : selectedOpt)
      : (placeholder || tr('select'));

    return (
      <DropdownContainer ref={ref}>
        <DropdownTrigger
          $darkMode={dm}
          $scheme={sc}
          $isOpen={isOpen}
          $hasValue={!!selectedOpt}
          onClick={() => setIsOpen(!isOpen)}
        >
          {displayLabel}
        </DropdownTrigger>
        {isOpen && (
          <DropdownList $darkMode={dm} $scheme={sc}>
            {options.map(opt => {
              const val = typeof opt === 'object' ? opt.value : opt;
              const label = typeof opt === 'object' ? opt.label : opt;
              return (
                <DropdownItem
                  key={val}
                  $darkMode={dm}
                  $scheme={sc}
                  $isSelected={val === value}
                  onClick={() => { onChange(val); setIsOpen(false); }}
                >
                  {label}
                </DropdownItem>
              );
            })}
          </DropdownList>
        )}
      </DropdownContainer>
    );
  };

  const renderSelect = (label, value, onChange, options, placeholder, helpText) => (
    <SelectField>
      <Label $darkMode={darkMode}>{label}</Label>
      {helpText && <HelpText $darkMode={darkMode}>{helpText}</HelpText>}
      <CustomDropdown
        value={value ?? ""}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        darkMode={darkMode}
        scheme={scheme}
      />
    </SelectField>
  );

  const renderLangInputs = (testType, scores, onChange) => {
    if (!testType) return null;
    const abilities = ["speaking", "listening", "reading", "writing"];
    return (
      <LangGrid>
        {abilities.map(ab => {
          const opts = getTestOptions(testType, ab);
          return renderSelect(
            tr(ab),
            scores?.[ab] ?? "",
            v => onChange({ ...scores, [ab]: v }),
            opts, tr('score')
          );
        })}
      </LangGrid>
    );
  };

  const renderStep = () => {
    switch (currentStepName) {
      case "marital":
        return (<>
          <StepHeader>
            <StepTitle $darkMode={darkMode}><StepNumber $scheme={scheme}>1</StepNumber>{tr('maritalTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('maritalSubtitle')}</StepSubtitle>
          </StepHeader>
          {renderSelect(tr('maritalQuestion'), answers.maritalStatus, v => update("maritalStatus", v), [
            { value: "single", label: tr('maritalSingle') },
            { value: "married", label: tr('maritalMarried') },
            { value: "commonlaw", label: tr('maritalCommonLaw') },
            { value: "divorced", label: tr('maritalDivorced') },
            { value: "widowed", label: tr('maritalWidowed') },
            { value: "annulled", label: tr('maritalAnnulled') },
          ])}
          {(answers.maritalStatus === "married" || answers.maritalStatus === "commonlaw") && (<>
            {renderSelect(tr('spouseIsCitizen'), answers.spouseIsCitizen, v => update("spouseIsCitizen", v), YES_NO(tr))}
            {answers.spouseIsCitizen === "no" && renderSelect(tr('spouseComing'), answers.spouseComing, v => update("spouseComing", v), YES_NO(tr))}
          </>)}
        </>);

      case "age":
        return (<>
          <StepHeader>
            <StepTitle $darkMode={darkMode}><StepNumber $scheme={scheme}>2</StepNumber>{tr('ageTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('ageSubtitle')}</StepSubtitle>
          </StepHeader>
          {renderSelect(tr('ageQuestion'), answers.age, v => update("age", v),
            Array.from({ length: 63 }, (_, i) => {
              const age = i + 17;
              const key = age <= 17 ? 'ageYearsOrLess' : age >= 45 ? 'ageYearsOrMore' : 'ageYears';
              return { value: String(age), label: tr(key, { age }) };
            })
          )}
        </>);

      case "education":
        return (<>
          <StepHeader>
            <StepTitle $darkMode={darkMode}><StepNumber $scheme={scheme}>3</StepNumber>{tr('educationTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('educationSubtitle')}</StepSubtitle>
          </StepHeader>
          {renderSelect(tr('educationQuestion'), answers.education, v => update("education", v), eduLevels)}
          {answers.education && answers.education !== "none" && (<>
            {renderSelect(tr('hasCanadianEdu'), answers.hasCanadianEdu, v => update("hasCanadianEdu", v),
              YES_NO(tr), null, tr('hasCanadianEduHelp'))}
            {answers.hasCanadianEdu === "yes" && renderSelect(tr('canadianEduLevel'), answers.canadianEduLevel, v => update("canadianEduLevel", v), [
              { value: "secondary_or_less", label: tr('canEduSecondary') },
              { value: "short", label: tr('canEduShort') },
              { value: "long", label: tr('canEduLong') },
            ])}
          </>)}
        </>);

      case "language_first":
        return (<>
          <StepHeader>
            <StepTitle $darkMode={darkMode}><StepNumber $scheme={scheme}>4</StepNumber>{tr('firstLangTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('firstLangSubtitle')}</StepSubtitle>
          </StepHeader>
          {renderSelect(tr('firstLangQuestion'), answers.firstLangTest,
            v => { update("firstLangTest", v); update("firstLangScores", {}); },
            ["CELPIP-G", "IELTS", "PTE Core", "TEF Canada", "TCF Canada"]
          )}
          {answers.firstLangTest && renderLangInputs(answers.firstLangTest, answers.firstLangScores, s => update("firstLangScores", s))}
        </>);

      case "language_second":
        return (<>
          <StepHeader>
            <StepTitle $darkMode={darkMode}><StepNumber $scheme={scheme}>5</StepNumber>{tr('secondLangTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('secondLangSubtitle')}</StepSubtitle>
          </StepHeader>
          {renderSelect(tr('secondLangQuestion'), answers.hasSecondLang ? "yes" : "no",
            v => { update("hasSecondLang", v === "yes"); if (v === "no") { update("secondLangTest", ""); update("secondLangScores", {}); } },
            NO_YES(tr)
          )}
          {answers.hasSecondLang && (<>
            {renderSelect(tr('secondLangWhich'), answers.secondLangTest,
              v => { update("secondLangTest", v); update("secondLangScores", {}); },
              isFrenchTest(answers.firstLangTest) ? ["CELPIP-G", "IELTS", "PTE Core"] : ["TEF Canada", "TCF Canada"]
            )}
            {answers.secondLangTest && renderLangInputs(answers.secondLangTest, answers.secondLangScores, s => update("secondLangScores", s))}
          </>)}
        </>);

      case "work":
        return (<>
          <StepHeader>
            <StepTitle $darkMode={darkMode}><StepNumber $scheme={scheme}>6</StepNumber>{tr('workTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('workSubtitle')}</StepSubtitle>
          </StepHeader>
          {renderSelect(tr('canadianWork'), String(answers.canadianWork), v => update("canadianWork", parseInt(v)), YEARS_5(tr))}
          {lmiInForce && answers.canadianWork >= 1 && (
            <CrsNocPicker
              label={tr('lmiWorkNoc')} helpText={tr('lmiWorkNocHelp')}
              value={answers.highWageWorkNoc} title={answers.highWageWorkTitle}
              onChange={(code, title) => { update("highWageWorkNoc", code); update("highWageWorkTitle", title); }}
              tr={tr} darkMode={darkMode} scheme={scheme}
            />
          )}
          {lmiInForce && renderSelect(tr('lmiJobOffer'), answers.jobOffer ? "yes" : "no",
            v => { update("jobOffer", v === "yes"); if (v === "no") { update("jobOfferNoc", ""); update("jobOfferTitle", ""); } },
            NO_YES(tr), null, tr('lmiJobOfferHelp')
          )}
          {lmiInForce && answers.jobOffer && (
            <CrsNocPicker
              label={tr('lmiJobOfferNoc')}
              value={answers.jobOfferNoc} title={answers.jobOfferTitle}
              onChange={(code, title) => { update("jobOfferNoc", code); update("jobOfferTitle", title); }}
              tr={tr} darkMode={darkMode} scheme={scheme}
            />
          )}
          {renderSelect(tr('foreignWork'), String(answers.foreignWork), v => update("foreignWork", parseInt(v)), YEARS_3(tr))}
          {renderSelect(tr('certQual'), answers.certQual ? "yes" : "no",
            v => update("certQual", v === "yes"),
            NO_YES(tr), null, tr('certQualHelp')
          )}
        </>);

      case "additional":
        return (<>
          <StepHeader>
            <StepTitle $darkMode={darkMode}><StepNumber $scheme={scheme}>7</StepNumber>{tr('additionalTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('additionalSubtitle')}</StepSubtitle>
          </StepHeader>
          {renderSelect(tr('pnpQuestion'), answers.pnp ? "yes" : "no", v => update("pnp", v === "yes"), NO_YES(tr))}
          {renderSelect(tr('siblingQuestion'), answers.sibling ? "yes" : "no", v => update("sibling", v === "yes"), NO_YES(tr))}
        </>);

      case "spouse":
        return (<>
          <StepHeader>
            <StepTitle $darkMode={darkMode}><StepNumber $scheme={scheme}>8</StepNumber>{tr('spouseTitle')}</StepTitle>
            <StepSubtitle $darkMode={darkMode}>{tr('spouseSubtitle')}</StepSubtitle>
          </StepHeader>
          {renderSelect(tr('spouseEducation'), answers.spouseEducation, v => update("spouseEducation", v), eduLevels)}
          {renderSelect(tr('spouseCanWork'), String(answers.spouseCanWork), v => update("spouseCanWork", parseInt(v)), YEARS_5(tr))}
          {renderSelect(tr('spouseLangTest'), answers.spouseLangTest,
            v => { update("spouseLangTest", v); update("spouseLangScores", {}); },
            [{ value: "", label: tr('notApplicable') }, ...["CELPIP-G", "IELTS", "PTE Core", "TEF Canada", "TCF Canada"].map(t => ({ value: t, label: t }))]
          )}
          {answers.spouseLangTest && renderLangInputs(answers.spouseLangTest, answers.spouseLangScores, s => update("spouseLangScores", s))}
        </>);

      default:
        return null;
    }
  };

  const pct = Math.round(((step + 1) / totalSteps) * 100);

  return (
    <Wrapper>
      <ProgressBarContainer>
        <ProgressLabel $darkMode={darkMode}>
          <span>{tr('stepOf', { current: step + 1, total: totalSteps })}</span>
          <span>{pct}%</span>
        </ProgressLabel>
        <ProgressBarTrack $darkMode={darkMode}>
          <ProgressBarFill $pct={pct} $scheme={scheme} />
        </ProgressBarTrack>
      </ProgressBarContainer>

      {renderStep()}

      <NavRow>
        <BackBtn $darkMode={darkMode} onClick={goBack}>
          {step === 0 ? tr('cancel') : tr('back')}
        </BackBtn>
        <NextBtn $darkMode={darkMode} $scheme={scheme} disabled={!canProceed} onClick={goNext}>
          {step === actualSteps.length - 1 ? tr('calculate') : tr('next')}
        </NextBtn>
      </NavRow>
    </Wrapper>
  );
};

export default CRSCalculator;
